import React from 'react';
import cn from 'classnames';

import ContactForm from '../contact-form/contact-form';
import ContentWrapper from '../content-wrapper/content-wrapper';
import FooterGroup from '../footer-group/footer-group';
import untypedHeading from '../heading/heading';
import untypedLink from '../link/link';
import Logo from '../logo/logo';
import RichText from '../rich-text/rich-text';
import SocialMediaLink from '../social-media-link/social-media-link';
import TabTrapper from '../tab-trapper/tab-trapper';

import useToggle from 'hooks/use-toggle';

import { FooterProps } from '../../Models/Generated/FooterProps';

// Loose typing for legacy jsx components whose inferred prop types are stricter
// than their actual runtime contract (destructured props without defaults)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LegacyComponent = React.ComponentType<any> & Record<string, any>;

const Heading = untypedHeading as LegacyComponent;
const Link = untypedLink as LegacyComponent;

type Props = FooterProps & {
  theme?: string;
};

const Footer = ({
  contacts = [],
  groups = [],
  socialMediaTitle,
  socialMediaLinks = [],
  theme
}: Props) => {
  const [contactVisible, _toggleContact, hideContact, showContact] = useToggle(
    false
  );

  return (
    <footer
      id="id-footer-hotjar"
      className={cn('footer', { [`footer__${theme}`]: theme })}
    >
      <ContentWrapper className="footer__content">
        <div className="footer__logo-container">
          <Logo theme={Logo.themes.white}></Logo>
        </div>

        <div className="footer__contacts-container">
          <div className="footer__contacts">
            {contacts.map(contact => {
              return (
                <div
                  className="footer__contact"
                  key={contact.form ? contact.form.title : contact.title}
                >
                  {contact.title && (
                    <div className="footer__contact-title">
                      <Heading
                        as="div"
                        theme={Heading.themes.h5}
                        color={Heading.colors.white}
                      >
                        {contact.title}
                      </Heading>
                    </div>
                  )}

                  <div className="footer__contact-item">
                    {contact.link && (
                      <Link
                        animation="shrink"
                        color="yellow"
                        fontTheme="regular"
                        {...contact.link}
                      ></Link>
                    )}
                    {contact.text && (
                      <div className="footer__contact-text">
                        <RichText
                          {...contact.text}
                          themes={[RichText.themes.footer]}
                        ></RichText>
                      </div>
                    )}

                    {contact.form && (
                      <button
                        className="footer__contact-form-button"
                        onClick={showContact}
                        type="button"
                      >
                        <span className="footer__contact-form-button-span">
                          {contact.contactFormText}
                        </span>
                      </button>
                    )}
                    {contact.form && (
                      <div className="footer__contact-form-container">
                        <TabTrapper isActive={contactVisible}>
                          {contactVisible && (
                            <ContactForm hide={hideContact} {...contact.form} />
                          )}
                        </TabTrapper>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {
            <div className="footer__social-media-container">
              <div className="footer__social-media">
                <Heading
                  as="div"
                  theme={Heading.themes.h5}
                  color={Heading.colors.white}
                >
                  {socialMediaTitle}
                </Heading>
                <div className="footer__social-media-icons">
                  {socialMediaLinks.map(link => (
                    <SocialMediaLink key={link.text + link.url} {...link} />
                  ))}
                </div>
              </div>
            </div>
          }
        </div>
        <div className="footer__menu">
          {groups.map(group => (
            <FooterGroup key={group.title} {...group} />
          ))}
        </div>
      </ContentWrapper>
    </footer>
  );
};

export default Footer;
